import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { useSalon } from "./SalonContext";
import { useApp } from "./AppContext";
import { useFeatureAccess } from "./FeatureAccessContext";
import { StaffHoursOverride } from "../types/scheduleAdjustment";
import moment from "moment";
import {
  CalendarViewType,
  WeekDisplay,
  CalendarContextState,
  DateRange,
  StaffServiceMapping,
  AppointmentStatus,
  CALENDAR_STORAGE_KEYS,
  DEFAULT_SELECTED_STATUSES,
  clampZoom,
} from "../types/calendar";
import { Employee, SalonService } from "../types/salon";
import { getEffectiveScope } from "../utils/permissionFilters";
// @ts-ignore — staffUtils.js predates the TS migration
import { isBookableActiveEmployee } from "../utils/staffUtils";

const CalendarContext = createContext<CalendarContextState | null>(null);

interface CalendarProviderProps {
  children: ReactNode;
}

// Helper to get initial state from localStorage
const getStoredValue = <T,>(key: string, defaultValue: T): T => {
  if (typeof window === "undefined") return defaultValue;
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper to store value in localStorage
const setStoredValue = <T,>(key: string, value: T): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore localStorage errors
  }
};

/** Stored week display; anything but "split" reads as "combined". */
export const readInitialWeekDisplay = (): WeekDisplay =>
  getStoredValue<string>(CALENDAR_STORAGE_KEYS.WEEK_DISPLAY, "") === "split"
    ? "split"
    : "combined";

/** Stored grid zoom, clamped to the current range; anything else reads as 1. */
export const readInitialZoom = (): number => {
  const stored = getStoredValue<unknown>(CALENDAR_STORAGE_KEYS.ZOOM, 1);
  return typeof stored === "number" && Number.isFinite(stored)
    ? clampZoom(stored)
    : 1;
};

/**
 * Status selection to start from. A selection saved before "Arrived" existed
 * gets it added exactly once (tracked by a flag key), so Arrived appointments
 * aren't silently hidden on Day/Week/List/mobile; a user who later deselects
 * Arrived keeps that choice. With nothing stored the defaults (which include
 * Arrived) apply. Writes are idempotent, so a double-invoked initializer
 * (StrictMode) is harmless.
 */
export const readInitialSelectedStatuses = (): AppointmentStatus[] => {
  const stored = getStoredValue<AppointmentStatus[] | null>(
    CALENDAR_STORAGE_KEYS.SELECTED_STATUSES,
    null,
  );
  if (!Array.isArray(stored)) return DEFAULT_SELECTED_STATUSES;

  const migrated = getStoredValue<boolean>(
    CALENDAR_STORAGE_KEYS.SELECTED_STATUSES_ARRIVED_MIGRATED,
    false,
  );
  if (migrated || stored.includes("Arrived")) {
    if (!migrated) {
      setStoredValue(
        CALENDAR_STORAGE_KEYS.SELECTED_STATUSES_ARRIVED_MIGRATED,
        true,
      );
    }
    return stored;
  }

  const confirmedAt = stored.indexOf("Confirmed");
  const next: AppointmentStatus[] =
    confirmedAt === -1
      ? [...stored, "Arrived"]
      : [
          ...stored.slice(0, confirmedAt + 1),
          "Arrived",
          ...stored.slice(confirmedAt + 1),
        ];
  setStoredValue(CALENDAR_STORAGE_KEYS.SELECTED_STATUSES, next);
  setStoredValue(
    CALENDAR_STORAGE_KEYS.SELECTED_STATUSES_ARRIVED_MIGRATED,
    true,
  );
  return next;
};

export function CalendarProvider({ children }: CalendarProviderProps) {
  const { services, employees, currentUser, businessInfo } = useSalon() as {
    services: SalonService[] | undefined;
    employees: Employee[] | undefined;
    currentUser: { id: string } | undefined;
    businessInfo: { id?: string } | null;
  };
  const { access } = useFeatureAccess();
  const { getSalonHoursOverrides } = useApp();

  // Get calendar scope for filtering staff columns
  const calendarScope = getEffectiveScope(
    access?.section_permissions?.calendar?.scope as "own" | "all" | undefined,
  );
  const currentUserId = currentUser?.id || "";

  const [staffServices, setStaffServices] = useState<
    StaffServiceMapping[] | undefined
  >();

  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [selectedArtists, setSelectedArtists] = useState<string[]>(["all"]);
  const [selectedService, setSelectedService] = useState<string>("all");

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // New state for redesign - with localStorage persistence
  const [viewType, setViewTypeState] = useState<CalendarViewType>(() =>
    getStoredValue<CalendarViewType>(CALENDAR_STORAGE_KEYS.VIEW_TYPE, "day"),
  );
  const [weekDisplay, setWeekDisplayState] = useState<WeekDisplay>(
    readInitialWeekDisplay,
  );
  // RBC's internal day/week view. Seeded from the stored viewType and kept in
  // step by setViewType (no mirror effect).
  const [selectedView, setSelectedView] = useState<"day" | "week">(() =>
    viewType === "week" ? "week" : "day",
  );

  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([
    "all",
  ]);

  const [selectedStatuses, setSelectedStatusesState] = useState<
    AppointmentStatus[]
  >(readInitialSelectedStatuses);

  // Persist viewType to localStorage
  const setViewType = useCallback((type: CalendarViewType) => {
    setViewTypeState(type);
    setStoredValue(CALENDAR_STORAGE_KEYS.VIEW_TYPE, type);

    // Sync selectedView for react-big-calendar when using day/week
    if (type === "day" || type === "week") {
      setSelectedView(type);
    }
  }, []);

  // Persist the grid zoom to localStorage
  const [zoom, setZoomState] = useState<number>(readInitialZoom);
  const setZoom = useCallback((z: number) => {
    const clamped = clampZoom(z);
    setZoomState(clamped);
    setStoredValue(CALENDAR_STORAGE_KEYS.ZOOM, clamped);
  }, []);

  // Persist the week display to localStorage
  const setWeekDisplay = useCallback((display: WeekDisplay) => {
    setWeekDisplayState(display);
    setStoredValue(CALENDAR_STORAGE_KEYS.WEEK_DISPLAY, display);
  }, []);

  // Persist selectedStatuses to localStorage
  const setSelectedStatuses = useCallback((statuses: AppointmentStatus[]) => {
    setSelectedStatusesState(statuses);
    setStoredValue(CALENDAR_STORAGE_KEYS.SELECTED_STATUSES, statuses);
    // A selection saved from here already reflects Arrived; never migrate it.
    setStoredValue(
      CALENDAR_STORAGE_KEYS.SELECTED_STATUSES_ARRIVED_MIGRATED,
      true,
    );
  }, []);

  const [dateRange, setDateRange] = useState<DateRange>({
    start: moment().startOf("week").format("YYYY-MM-DD"),
    end: moment().endOf("week").add(1, "day").format("YYYY-MM-DD"),
  });

  // Update date range when selectedDate leaves it (pure updater: keeps the
  // current range, including one set explicitly via setDateRange, while the
  // selected date is still inside it).
  useEffect(() => {
    const start = moment(selectedDate).startOf("week").format("YYYY-MM-DD");
    const end = moment(selectedDate)
      .endOf("week")
      .add(1, "day")
      .format("YYYY-MM-DD");

    setDateRange((prev) =>
      !prev.start ||
      !prev.end ||
      moment(selectedDate).isBefore(prev.start) ||
      moment(selectedDate).isAfter(prev.end)
        ? { start, end }
        : prev,
    );
  }, [selectedDate]);

  // "Working today" display filter — restricts the staff list, calendar columns
  // (day view), and events (both views) to staff who have working hours on the
  // currently-viewed date (weekly schedule + ad-hoc overrides). Not persisted.
  const [workingToday, setWorkingToday] = useState<boolean>(false);

  // Ad-hoc hours overrides for every staff member across `dateRange` (the
  // viewed week), in one salon-scoped fetch. This provider is the ONLY owner of
  // this fetch: DayView reads it for the working-hours shading and
  // workingStaffIds reads it for "Working today". Fetched only while one of
  // those consumers needs it (day view, or the filter is on). Since dateRange
  // always contains selectedDate, the viewed date's overrides are included.
  // getSalonHoursOverrides isn't memoized in AppContext — read it through a ref
  // so the effect's deps stay salon + range only.
  const [hoursOverrides, setHoursOverrides] = useState<StaffHoursOverride[]>(
    [],
  );
  const getOverridesRef = useRef(getSalonHoursOverrides);
  useEffect(() => {
    getOverridesRef.current = getSalonHoursOverrides;
  }, [getSalonHoursOverrides]);
  const salonId = businessInfo?.id;
  // Consumers: day view shading, Split week shading, "Working today".
  const needsOverrides =
    viewType === "day" ||
    (viewType === "week" && weekDisplay === "split") ||
    workingToday;
  const rangeStart = dateRange.start;
  const rangeEnd = dateRange.end;

  useEffect(() => {
    if (!needsOverrides || !salonId || !rangeStart || !rangeEnd) {
      setHoursOverrides([]);
      return;
    }
    let cancelled = false;
    getOverridesRef
      .current(salonId, rangeStart, rangeEnd)
      .then((overrides) => {
        if (!cancelled) setHoursOverrides(overrides);
      })
      .catch(() => {
        // Non-fatal: fall back to the weekly schedule if the fetch fails.
        if (!cancelled) setHoursOverrides([]);
      });
    return () => {
      cancelled = true;
    };
  }, [needsOverrides, salonId, rangeStart, rangeEnd]);

  // Staff working on the viewed date: an override for that date wins (both times
  // present = working, both null = day off); otherwise the weekly schedule for
  // that weekday (a start time set for the day = working).
  const viewedDateStr = moment(selectedDate).format("YYYY-MM-DD");
  const workingStaffIds = useMemo(() => {
    const set = new Set<string>();
    const weekday = moment(viewedDateStr, "YYYY-MM-DD")
      .format("dddd")
      .toLowerCase();
    (employees || []).forEach((emp) => {
      const override = hoursOverrides.find(
        (o) => o.staff_id === emp.id && o.work_date === viewedDateStr,
      );
      const working = override
        ? !!(override.start_time && override.end_time)
        : !!(emp as unknown as Record<string, string | undefined>)[
            `${weekday}_start_time`
          ];
      if (working) set.add(emp.id);
    });
    return set;
  }, [employees, hoursOverrides, viewedDateStr]);

  // Build staff services mapping
  useEffect(() => {
    if (services) {
      const mappedStaffServices: StaffServiceMapping[] = services.flatMap(
        (salonService) =>
          (salonService.servicing_staff || []).map((staffService) => ({
            id: staffService.id,
            salonServiceId: salonService.id,
            staffId: staffService.staff_id,
          })),
      );
      setStaffServices(mappedStaffServices);
    }
  }, [services]);

  // Filter employees based on selected artists AND calendar.scope permission
  useEffect(() => {
    if (employees) {
      let activeEmployees = employees.filter(isBookableActiveEmployee);

      // Apply scope-based filtering: if scope is "own", only show current user's column
      if (calendarScope === "own" && currentUserId) {
        activeEmployees = activeEmployees.filter(
          (emp) => emp.id === currentUserId,
        );
      }

      if (selectedArtists.includes("all")) {
        setFilteredEmployees(activeEmployees);
      } else {
        setFilteredEmployees(
          activeEmployees.filter((employee) =>
            selectedArtists.includes(employee.id),
          ),
        );
      }
    }
  }, [selectedArtists, employees, calendarScope, currentUserId]);

  // [LOOP] churn probe
  useValueChurnProbe("CalendarContext", {
    dateRange,
    staffServices,
    filteredEmployees,
  });

  // Memoized so consumers don't re-render on every provider render; setters
  // are useState setters or useCallback'd wrappers, so deps are stable.
  const value: CalendarContextState = useMemo(
    () => ({
      // Existing state
      staffServices,
      selectedArtists,
      selectedService,
      filteredEmployees,
      selectedDate,
      selectedView,
      dateRange,

      // New state for redesign
      viewType,
      weekDisplay,
      zoom,
      selectedServiceTypes,
      selectedStatuses,
      workingToday,
      workingStaffIds,
      hoursOverrides,

      // Setters
      setWorkingToday,
      setSelectedArtists,
      setSelectedService,
      setSelectedDate,
      setSelectedView,
      setDateRange,
      setViewType,
      setWeekDisplay,
      setZoom,
      setSelectedServiceTypes,
      setSelectedStatuses,
    }),
    [
      staffServices,
      selectedArtists,
      selectedService,
      filteredEmployees,
      selectedDate,
      selectedView,
      dateRange,
      viewType,
      weekDisplay,
      zoom,
      selectedServiceTypes,
      selectedStatuses,
      workingToday,
      workingStaffIds,
      hoursOverrides,
      setViewType,
      setWeekDisplay,
      setZoom,
      setSelectedStatuses,
    ],
  );

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

export const useCalendar = (): CalendarContextState => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error("useCalendar must be used within a CalendarProvider");
  }
  return context;
};
